import * as React from 'react';
import type { ReactNode } from 'react';
import type { showSuccessFlag as showSuccessFlagType } from '@helpcenter/flags';
import type { WrappedComponentProps, IntlShape } from 'react-intl-next';
import { injectIntl, useIntl } from 'react-intl-next';
import type {
    hideFeedbackCollector as hideFeedbackCollectorType,
    updateAdditionalFieldsInFeedbackCollector as updateAdditionalFieldsInFeedbackCollectorType,
} from 'state/actions/feedback-collector';
import type { FeedbackCollectorUIState } from 'state/ui/feedback-collector';
import fg from '@atlaskit/feature-gate-js-client';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { withAdditionalFieldsComponent } from '../with-additional-fields-component';
import { linkToSupport, feedbackWidgetData } from './constants';
import messages from './messages';
export interface Props {
    accountId: string | undefined;
    name: string;
    isOpen: FeedbackCollectorUIState['shouldOpenFeedbackWidget'];
    showTypeField: FeedbackCollectorUIState['showTypeField'];
    additionalFields: FeedbackCollectorUIState['additionalFields'];
    CustomComponent?: FeedbackCollectorUIState['CustomComponent'];
    hideFeedbackCollector: typeof hideFeedbackCollectorType;
    updateAdditionalFieldsInFeedbackCollector: typeof updateAdditionalFieldsInFeedbackCollectorType;
    showSuccessFlag: typeof showSuccessFlagType;
}

const getFeedbackGroupLabels = (intl: IntlShape) => {
    return {
        question: {
            fieldLabel: intl.formatMessage(messages.questionFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.questionSelectOptionLabel),
        },
        comment: {
            fieldLabel: intl.formatMessage(messages.commentFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.commentSelectOptionLabel),
        },
        bug: {
            fieldLabel: intl.formatMessage(messages.bugFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.bugSelectOptionLabel),
        },
        suggestion: {
            fieldLabel: intl.formatMessage(messages.suggestionFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.suggestionSelectOptionLabel),
        },
        empty: {
            fieldLabel: intl.formatMessage(messages.emptyFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.emptySelectOptionLabel),
        },
    };
};

const FeedbackPreamble = (props: { CustomComponent?: React.FC }) => {
    const { formatMessage } = useIntl();
    const CustomComponent = props.CustomComponent;
    return (
        <>
            {fg.checkGate('jsm_rename_product_to_app')
                ? formatMessage(messages.feedbackPreambleTitleTextAppify, {
                      // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
                      p: (chunks: ReactNode) => <p>{chunks}</p>,
                      br: <br />,
                  })
                : formatMessage(messages.feedbackPreambleTitleText, {
                      // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
                      p: (chunks: ReactNode) => <p>{chunks}</p>,
                      br: <br />,
                  })}
            {CustomComponent && <CustomComponent />}
        </>
    );
};

const CanBeContactedLabel = () => {
    const { formatMessage } = useIntl();
    return (
        // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
        <p>
            {formatMessage(messages.canContactLabelText, {
                a: (chunks: ReactNode) => (
                    <a href={linkToSupport} target="_blank" rel="noopener noreferrer">
                        {chunks}
                    </a>
                ),
            })}
        </p>
    );
};

const EnrolLabel = () => {
    const { formatMessage } = useIntl();
    return (
        // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
        <p>
            {fg.checkGate('jsm_rename_product_to_app')
                ? formatMessage(messages.enrolLabelTextAppify, {
                      a: (chunks: ReactNode) => (
                          <a href={linkToSupport} target="_blank" rel="noopener noreferrer">
                              {chunks}
                          </a>
                      ),
                  })
                : formatMessage(messages.enrolLabelText, {
                      a: (chunks: ReactNode) => (
                          <a href={linkToSupport} target="_blank" rel="noopener noreferrer">
                              {chunks}
                          </a>
                      ),
                  })}
        </p>
    );
};

export const DisplayFeedback: React.FC<Props & WrappedComponentProps> = ({
    accountId,
    name,
    isOpen,
    showTypeField,
    additionalFields,
    CustomComponent,
    intl,
    hideFeedbackCollector,
    showSuccessFlag,
    updateAdditionalFieldsInFeedbackCollector,
}) => {
    const close = React.useCallback(() => {
        hideFeedbackCollector();
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const displayFlag = React.useCallback(() => {
        showSuccessFlag(
            messages.feedbackSentFlagTitle,
            undefined,
            fg.checkGate('jsm_rename_product_to_app')
                ? messages.feedbackSentFlagDescriptionAppify
                : messages.feedbackSentFlagDescription
        );
    }, [showSuccessFlag]);

    const onAdditionalFieldChange = React.useCallback(
        (fieldId: string, fieldValue: string | object) => {
            const fields = { ...additionalFields };
            fields[fieldId] = fieldValue;
            updateAdditionalFieldsInFeedbackCollector(fields);
        },
        // oxlint-disable-next-line react-hooks/exhaustive-deps
        [additionalFields]
    );

    return (
        <div data-testid="portal-feedback-collector">
            {isOpen && (
                <FeedbackCollector
                    onClose={close}
                    onSubmit={displayFlag}
                    atlassianAccountId={accountId}
                    name={name}
                    entrypointId={feedbackWidgetData.entrypointId}
                    feedbackTitle={intl.formatMessage(messages.feedbackTitle)}
                    showTypeField={showTypeField}
                    feedbackTitleDetails={
                        <FeedbackPreamble
                            CustomComponent={
                                CustomComponent
                                    ? withAdditionalFieldsComponent({
                                          additionalFields,
                                          onChange: onAdditionalFieldChange,
                                      })(CustomComponent)
                                    : undefined
                            }
                        />
                    }
                    canBeContactedLabel={<CanBeContactedLabel />}
                    enrolInResearchLabel={<EnrolLabel />}
                    summaryPlaceholder={intl.formatMessage(messages.summaryPlaceholder)}
                    submitButtonLabel={intl.formatMessage(messages.submitButtonLabel)}
                    cancelButtonLabel={intl.formatMessage(messages.cancelButtonLabel)}
                    feedbackGroupLabels={getFeedbackGroupLabels(intl)}
                />
            )}
        </div>
    );
};

export default injectIntl(DisplayFeedback);
